import { type ItemDetails, type LegacySegmentTypes } from '@1debit/analytics';
import React, {
	type ForwardedRef,
	forwardRef,
	type HTMLAttributes,
	type MouseEventHandler,
	type ReactNode,
} from 'react';

import { type AnalyticsId } from './AnalyticsProvider/AnalyticsProvider';
import { useClickableElementAnalytics } from './AnalyticsProvider/hooks/useClickableElementAnalytics';

import { classNames } from '../server/utils/classNames';

// ClickableAnalyticsSlot is our variation of a Slot component used for the asChild pattern https://www.jacobparis.com/content/react-as-child

export type AsChildProps<DefaultElementProps> =
	| ({ asChild?: false } & DefaultElementProps)
	| { asChild: true; children: React.ReactNode };

type Props = React.HTMLAttributes<HTMLElement> & {
	trackElementType:
		| LegacySegmentTypes.ElementType.ELEMENT_TYPE_BUTTON
		| LegacySegmentTypes.ElementType.ELEMENT_TYPE_LINK;
	analyticsId: AnalyticsId;
	children: ReactNode;
	asChild?: boolean;
	defaultElement: 'button' | 'a';
	itemDetails: ItemDetails | undefined;
	itemValue: string | undefined;
	disabled?: boolean;
};

export const ClickableAnalyticsSlot = forwardRef(
	(
		{
			children,
			trackElementType,
			analyticsId,
			asChild,
			defaultElement,
			onClick,
			itemDetails,
			itemValue,
			disabled,
			...props
		}: Props,
		ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
	) => {
		const { handleClick } = useClickableElementAnalytics(analyticsId, trackElementType, itemDetails, itemValue);

		if (!asChild && defaultElement === 'a') {
			return (
				// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
				<a {...props} ref={ref as ForwardedRef<HTMLAnchorElement>} onClick={handleClick(onClick)}>
					{children}
				</a>
			);
		}
		if (!asChild && defaultElement === 'button') {
			return (
				<button
					{...props}
					disabled={disabled}
					ref={ref as ForwardedRef<HTMLButtonElement>}
					onClick={handleClick(onClick)}>
					{children}
				</button>
			);
		}

		if (React.isValidElement<HTMLAttributes<HTMLElement>>(children)) {
			const onClick = children.props.onClick as MouseEventHandler | undefined;

			const baseProps = {
				...props,
				...children.props,
				style: {
					...props.style,
					...children.props.style,
				},
				className: classNames(props.className, children.props.className),
			};

			if (disabled) {
				return React.cloneElement(children, {
					...baseProps,
					'aria-disabled': true,
					tabIndex: -1,
					href: undefined,
					onClick: undefined,
				} as React.HTMLAttributes<HTMLElement>);
			}

			return React.cloneElement(children, {
				...baseProps,
				onClick: handleClick(onClick),
			});
		}

		if (React.Children.count(children) > 1) {
			React.Children.only(null);
		}

		return null;
	},
);
